import { useState, useEffect, useCallback, useRef } from 'react';

export interface GooglePlace {
  place_id: string;
  name: string;
  formatted_address: string;
  rating?: number;
  user_ratings_total?: number;
  photos?: { photo_reference: string; width: number; height: number }[];
  website?: string;
  formatted_phone_number?: string;
  opening_hours?: { open_now?: boolean; weekday_text?: string[] };
  geometry?: { location: { lat: number; lng: number } };
  types?: string[];
  price_level?: number;
}

export interface PlaceResult {
  id: string;
  name: string;
  location: string;
  city: string;
  address: string;
  rating: number;
  reviewCount: number;
  image: string;
  placeId: string;
  website?: string;
  mapsUrl: string;
  phone?: string;
  openNow?: boolean;
  priceLevel?: number;
  lat?: number;
  lng?: number;
  cuisines: string[];
  instagram?: string;
}

interface CacheEntry {
  data: PlaceResult[];
  timestamp: number;
}

const CACHE_KEY = 'dds_places_cache_v57_';
const CACHE_TTL = 1000 * 60 * 60 * 24; // 24 hours

// SSR/prerender guard: this hook is imported by pages that run inside a
// plain Node process during build-time prerendering, where `window`,
// `localStorage`, and relative-path `fetch` (e.g. `/api/places/search`)
// don't exist. Every browser-only call in this file is gated behind this
// flag so the prerender script can safely import + call useGooglePlaces
// without throwing or hanging on a network call — it resolves straight to
// the deduped STATIC_FALLBACK list below, which is exactly the real,
// SEO-relevant restaurant content search engines should see.
const IS_BROWSER = typeof window !== 'undefined' && typeof localStorage !== 'undefined';

function getCityFromAddress(address: string): string {
  const cities = ['Frisco', 'Plano', 'Irving', 'Lewisville', 'McKinney', 'Dallas', 'The Colony', 'Allen', 'Prosper', 'Carrollton', 'Richardson', 'Garland', 'Little Elm', 'Aubrey', 'Melissa', 'FlowerMound', 'Addison', 'Highland Village', 'Coppell'];
  const upper = address.toUpperCase();
  for (const city of cities) {
    if (upper.includes(city.toUpperCase())) return city;
  }
  return 'Dallas';
}

function inferCuisines(types: string[] = [], name: string = ''): string[] {
  const cuisines: string[] = [];
  const lowerName = name.toLowerCase();
  const lowerTypes = types.map(t => t.toLowerCase());

  if (lowerTypes.includes('south_indian_restaurant') || lowerName.includes('dosa') || lowerName.includes('idli') || lowerName.includes('south')) {
    cuisines.push('South Indian');
  }
  // North Indian is assigned explicitly via sortByPriority — do NOT auto-infer
  if (lowerTypes.includes('vegetarian_restaurant') || lowerName.includes('veg') || lowerTypes.includes('vegan_restaurant')) {
    cuisines.push('Vegetarian');
  }
  if (lowerName.includes('street') || lowerName.includes('chaat') || lowerName.includes('panipuri')) {
    cuisines.push('Street Food');
  }
  if (lowerName.includes('fusion') || lowerTypes.includes('fusion_restaurant')) {
    cuisines.push('Fusion');
  }
  if (cuisines.length === 0) {
    cuisines.push('Indian');
  }
  return cuisines;
}

// Corrects known misspellings coming back from the live Google Places API listing data.
const NAME_CORRECTIONS: Record<string, string> = {
  'hashtag indiaa': 'Hashtag India',
  'prism bar & grill': 'Birista House by PRISM BAR n GRILL',
  'prism bar and grill': 'Birista House by PRISM BAR n GRILL',
  'prism bar n grill': 'Birista House by PRISM BAR n GRILL',
};

export function normalizeName(name: string): string {
  return NAME_CORRECTIONS[name.trim().toLowerCase()] || name;
}

function mapPlaceToResult(place: GooglePlace): PlaceResult {
  const city = getCityFromAddress(place.formatted_address || '');
  return {
    id: place.place_id,
    name: normalizeName(place.name),
    location: city,
    city,
    address: place.formatted_address || '',
    rating: place.rating || 0,
    reviewCount: place.user_ratings_total || 0,
    image: place.photos && place.photos.length > 0
      ? `/api/places/photo?ref=${place.photos[0].photo_reference}&maxwidth=600`
      : 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=600&h=400&fit=crop',
    placeId: place.place_id,
    website: place.website,
    mapsUrl: `https://www.google.com/maps/place/?q=place_id:${place.place_id}`,
    phone: place.formatted_phone_number,
    openNow: place.opening_hours?.open_now,
    priceLevel: place.price_level,
    lat: place.geometry?.location.lat,
    lng: place.geometry?.location.lng,
    cuisines: inferCuisines(place.types, place.name),
  };
}

async function fetchPlacesFromAPI(locationFilter?: string): Promise<PlaceResult[]> {
  if (!IS_BROWSER) {
    // No live network fetch during Node-side prerendering — resolve to the
    // static fallback so the caller's promise still settles.
    return sortByPriority(STATIC_FALLBACK);
  }
  const params = new URLSearchParams();
  params.set('query', 'Indian restaurants in Dallas DFW');
  if (locationFilter && locationFilter !== 'All Locations') {
    params.set('location', locationFilter);
  }

  const res = await fetch(`/api/places/search?${params.toString()}`);
  if (!res.ok) {
    throw new Error(`Places API error: ${res.status}`);
  }
  const data = await res.json();

  if (!data.results || !Array.isArray(data.results)) {
    throw new Error('Invalid response from Places API');
  }

  // Sort alphabetically by name
  const sorted = data.results
    .map(mapPlaceToResult)
    .sort((a: PlaceResult, b: PlaceResult) =>
      a.name.localeCompare(b.name)
    );

  return sortByPriority(sorted);
}

function getCacheKey(location?: string): string {
  return `${CACHE_KEY}${location || 'all'}`;
}

function getCached(location?: string): PlaceResult[] | null {
  if (!IS_BROWSER) return null;
  try {
    const raw = localStorage.getItem(getCacheKey(location));
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    if (Date.now() - entry.timestamp > CACHE_TTL) {
      localStorage.removeItem(getCacheKey(location));
      return null;
    }
    // Run through sortByPriority to deduplicate and sort cached data
    return sortByPriority(entry.data);
  } catch {
    return null;
  }
}

function setCached(location: string | undefined, data: PlaceResult[]) {
  if (!IS_BROWSER) return;
  try {
    const entry: CacheEntry = { data, timestamp: Date.now() };
    localStorage.setItem(getCacheKey(location), JSON.stringify(entry));
  } catch {
    // ignore storage errors
  }
}

function sortByPriority(places: PlaceResult[]): PlaceResult[] {
  // Deduplicate by name + address (case-insensitive) to prevent duplicates
  // from live API + static fallback overlap or stale cache entries
  const seen = new Set<string>();
  const deduped: PlaceResult[] = [];
  for (const place of places) {
    const dedupKey = `${place.name.toLowerCase()}||${place.address.toLowerCase()}`;
    if (!seen.has(dedupKey)) {
      seen.add(dedupKey);
      deduped.push(place);
    }
  }

  // Strip "Bar & Grill" from Tantra and Sheesh
  for (const place of deduped) {
    const n = place.name.toLowerCase();
    if (n === 'tantra' || n === 'sheesh' || n.includes('tantra') || n.includes('sheesh')) {
      place.cuisines = place.cuisines.filter(c => c !== 'Bar & Grill');
    }
  }

  // Add "Hookah Lounge" to hookah lounge restaurants
  const hookahNames = new Set(['cavalli', 'ghazal lounge and bar', 'huq up', 'lapasha lounge', 'sheesh', 'sheesh eatery & lounge',
    'the mirage lounge', 'yum of india', 'elysium - live music bar']);
  for (const place of deduped) {
    if (hookahNames.has(place.name.toLowerCase())) {
      if (!place.cuisines.includes('Hookah Lounge')) {
        place.cuisines.push('Hookah Lounge');
      }
    }
  }

  // Add "North Indian" to North Indian restaurants
  const northIndianNames = new Set(['the delhi lounge', 'red hot chilli pepper', 'sigree grill', '101 paratha', 'red hot', 'sanjh', 'kailash parbat', 'gwalia', "rajula's kitchen", 'mnm lounge', 'delhi6', 'urban tadka', 'desi chowrasta']);
  for (const place of deduped) {
    if (northIndianNames.has(place.name.toLowerCase())) {
      if (!place.cuisines.includes('North Indian')) {
        place.cuisines.push('North Indian');
      }
    }
  }

  // Add "Vegetarian" to Vegetarian restaurants
  const vegetarianNames = new Set<string>(['kailash parbat', 'gwalia', "rajula's kitchen"]);
  for (const place of deduped) {
    if (vegetarianNames.has(place.name.toLowerCase())) {
      if (!place.cuisines.includes('Vegetarian')) {
        place.cuisines.push('Vegetarian');
      }
    }
  }

  // Add "Mediterranean" to Mediterranean restaurants
  const mediterraneanNames = new Set(['gilgamesh', 'habibi & spice', 'prime doner', 'al-amir']);
  for (const place of deduped) {
    if (mediterraneanNames.has(place.name.toLowerCase())) {
      if (!place.cuisines.includes('Mediterranean')) {
        place.cuisines.push('Mediterranean');
      }
    }
  }

  // Add "Pakistani" to Pakistani restaurants
  const pakistaniNames = new Set(['bk khan bbq', 'cafe de khan', 'mai colachi', 'al markaz']);
  for (const place of deduped) {
    if (pakistaniNames.has(place.name.toLowerCase())) {
      if (!place.cuisines.includes('Pakistani')) {
        place.cuisines.push('Pakistani');
      }
    }
  }

  // Any restaurant tagged "Pakistani" is also tagged "Halal"
  for (const place of deduped) {
    if (place.cuisines.includes('Pakistani') && !place.cuisines.includes('Halal')) {
      place.cuisines.push('Halal');
    }
  }

  // Add "South Indian" to South Indian restaurants
  const southIndianNames = new Set(['hyderabad house', 'hashtag india', 'rotate social', 'dil se desi', 'dum n grill', 'nags kitchen', 'yum of india', 'desibites', 'elysium - live music bar', 'house of biryanis and kebabs', 'marina', 'spicy vihaar', 'bawarchi', 'bawarchi biryanis', 'goli soda', 'swadeshi', 'mnm lounge', 'desi chowrasta', 'simply south']);
  for (const place of deduped) {
    if (southIndianNames.has(place.name.toLowerCase())) {
      if (!place.cuisines.includes('South Indian')) {
        place.cuisines.push('South Indian');
      }
    }
  }

  // Add "Fusion" to Fusion restaurants
  const fusionNames = new Set(['universal bakery', 'jashan', 'windmills', 'tantra', 'the monks']);
  for (const place of deduped) {
    if (fusionNames.has(place.name.toLowerCase())) {
      if (!place.cuisines.includes('Fusion')) {
        place.cuisines.push('Fusion');
      }
    }
  }

  // Add "Others" to Others restaurants
  const othersNames = new Set(['frisco basecamp']);
  for (const place of deduped) {
    if (othersNames.has(place.name.toLowerCase())) {
      if (!place.cuisines.includes('Others')) {
        place.cuisines.push('Others');
      }
    }
  }

  // Remove "Fusion" from specific restaurants
  const removeFusionNames = new Set(['rotate social', 'desibites', 'birista house by prism bar n grill', 'red hot chilli pepper', 'the retro street bar & kit', 'red hot', 'frisco basecamp']);
  for (const place of deduped) {
    if (removeFusionNames.has(place.name.toLowerCase())) {
      place.cuisines = place.cuisines.filter(c => c !== 'Fusion');
    }
  }

  // Remove specific restaurants
  const filtered = deduped.filter(p => {
    const n = p.name.toLowerCase();
    const a = p.address.toLowerCase();
    if (n === 'anjappar elite' && a === '6500 w park blvd, plano, tx 75093') return false;
    if (n === 'dil se desi' && a === '1500 w spring creek pkwy, plano, tx 75093') return false;
    if (n === 'the monks' && a === '26715 e university dr, aubrey, tx 76227') return false;
    if (n === 'the monks' && a === '190 e stacy rd, allen, tx 75002') return false;
    if (n === 'hashtag india' && a === '2625 old denton rd, irving, tx 75062') return false;
    if (n === 'hyderabad house' && a === '939 w stacy rd, allen, tx 75013') return false;
    if (n === 'hyderabad house' && a === '1000 w prosper trail, prosper, tx 75078') return false;
    if (n === 'gt road restaurant') return false;
    return true;
  });

  // Cards pinned to the very top in exact order
  const topOrder = [
    'spice rack',
    'simply south',
    'hashtag india',
    'the monks',
    'hyderabad house',
    'rotate social',
    'the mango yard',
    'dil se desi',
    'dum n grill',
    'nags kitchen',
    'house of biryani and kebabs',
    'universal bakery',
    'the delhi lounge',
    'anjappar elite',
    'mnm lounge',
    'soul of south',
    'yum of india',
    'desi chowrasta',
  ];
  const topSet = new Set(topOrder);

  // Restaurants to move right before Anjappar Elite (end of main group, before Bawarchi)
  const beforeAnjappar: Set<string> = new Set([
    'dum n grill',
  ]);

  // Restaurants to move to the very end (after Bawarchi)
  const moveToEnd = new Set([
    '101 paratha',
    'al markaz',
    'al-amir',
    'bharat bhavan',
    'bk khan bbq',
    'cafe de khan',
    'cavalli',
    'elysium',
    'frisco basecamp',
    'gilgamesh',
    'habibi & spice',
    'prime doner',
    'ghazal lounge and bar',
    'golisoda',
    'goli soda',
    'gwalia',
    'kailash parbat',
    'lapasha lounge',
    'mai colachi',
    'rajula\'s kitchen',
    'red hot',
    'sanjh',
    'saravanaa bhavan',
    'sheesh',
    'sheesh eatery & lounge',
    'sigree grill',
    'shiva\'s bar & grill',
    'swadeshi',
    'huq up',
    'the mirage lounge',
    'veg vihaar',
    'windmills',
    'delhi6',
    'jashan',
    'urban tadka',
    'spice rack plano',
  ]);

  // Split: topGroup → rest → beforeAnjappar → Bawarchi* → moveToEnd
  const topGroup: PlaceResult[] = [];
  const beforeAnjapparGroup: PlaceResult[] = [];
  const bawarchi: PlaceResult[] = [];
  const endGroup: PlaceResult[] = [];
  const rest: PlaceResult[] = [];
  for (const place of filtered) {
    const name = place.name.toLowerCase();
    if (topSet.has(name)) {
      topGroup.push(place);
    } else if (beforeAnjappar.has(name)) {
      beforeAnjapparGroup.push(place);
    } else if (name.startsWith('bawarchi')) {
      bawarchi.push(place);
    } else if (moveToEnd.has(name)) {
      endGroup.push(place);
    } else {
      rest.push(place);
    }
  }

  // Sort topGroup by exact topOrder, rest/beforeAnjappar/bawarchi/end alphabetically
  topGroup.sort((a, b) => topOrder.indexOf(a.name.toLowerCase()) - topOrder.indexOf(b.name.toLowerCase()));
  rest.sort((a, b) => a.name.localeCompare(b.name));
  beforeAnjapparGroup.sort((a, b) => a.name.localeCompare(b.name));
  bawarchi.sort((a, b) => a.name.localeCompare(b.name));
  endGroup.sort((a, b) => a.name.localeCompare(b.name));

  return [...topGroup, ...rest, ...beforeAnjapparGroup, ...bawarchi, ...endGroup];
}

// Static fallback data (current restaurant list enhanced with Google-like fields)
const STATIC_FALLBACK: PlaceResult[] = [
  {
    id: 'static-1',
    name: 'Spice Rack',
    location: 'Frisco',
    city: 'Frisco',
    address: '10010 Coit Rd, Frisco, TX 75035',
    rating: 4.5,
    reviewCount: 1200,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783461078615-6lc4w.png',
    placeId: 'static-1',
    mapsUrl: 'https://www.google.com/maps/search/Spice+Rack+10010+Coit+Rd+Frisco+TX+75035',
    cuisines: ['South Indian', 'Street Food'],
  },
  {
    id: 'static-2',
    name: 'House of Biryanis and Kebabs',
    location: 'Frisco',
    city: 'Frisco',
    address: '9101 Preston Rd, Frisco, TX 75033',
    rating: 4.3,
    reviewCount: 890,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782093325592-95c18t.jpg',
    placeId: 'static-2',
    mapsUrl: 'https://www.google.com/maps/search/House+of+Biryanis+and+Kebabs+9101+Preston+Rd+Frisco+TX+75033',
    cuisines: ['Biryani'],
  },
  {
    id: 'static-3',
    name: 'Dil se Desi',
    location: 'Plano',
    city: 'Plano',
    address: '8201 Ohio Dr #110, Plano, TX 75024',
    rating: 4.2,
    reviewCount: 750,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782092242877-c8dul.jpg',
    placeId: 'static-3',
    mapsUrl: 'https://www.google.com/maps/search/Dil+se+Desi+Plano+TX',
    cuisines: [],
  },
  {
    id: 'static-dumngrill',
    name: 'Dum N Grill',
    location: 'Melissa',
    city: 'Melissa',
    address: '3031 Washington Dr #200, Melissa, TX 75454',
    rating: 4.0,
    reviewCount: 200,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782144875940-9e9cb9.jpg',
    placeId: 'static-dumngrill',
    mapsUrl: 'https://www.google.com/maps/search/Dum+N+Grill+3031+Washington+Dr+Melissa+TX+75454',
    cuisines: ['Mughlai'],
  },
  {
    id: 'static-4',
    name: 'Hyderabad House',
    location: 'Irving',
    city: 'Irving',
    address: '949 W Royal Ln Ste # 108, Irving, TX 75039',
    rating: 4.4,
    reviewCount: 1100,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782091147428-1di86l.jpg',
    placeId: 'static-4',
    mapsUrl: 'https://www.google.com/maps/search/Hyderabad+House+Irving+TX',
    cuisines: ['South Indian'],
    instagram: 'https://www.instagram.com/p/DZ6LlyAM0tk/',
  } as PlaceResult & { instagram: string },
  {
    id: 'static-5',
    name: 'The Monks',
    location: 'Lewisville',
    city: 'Lewisville',
    address: '101 Castle Hills Dr, Suite #200, Lewisville, TX 75056',
    rating: 4.1,
    reviewCount: 520,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782090561864-13rclj.jpg',
    placeId: 'static-5',
    mapsUrl: 'https://www.google.com/maps/search/The+Monks+Lewisville+TX',
    cuisines: ['Fusion'],
  },
  {
    id: 'static-8',
    name: 'Simply South',
    location: 'McKinney',
    city: 'McKinney',
    address: '4781 S Custer Rd, McKinney, TX 75070',
    rating: 4.0,
    reviewCount: 430,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782091089501-g0rbai.jpg',
    placeId: 'static-8',
    mapsUrl: 'https://www.google.com/maps/search/Simply+South+McKinney+TX',
    cuisines: ['South Indian'],
  },
  {
    id: 'static-simply-south-irving',
    name: 'Simply South',
    location: 'Irving',
    city: 'Irving',
    address: '1001 MacArthur Park Dr, Irving, TX 75063',
    rating: 4.0,
    reviewCount: 350,
    image: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-24/1b6537bd39ab.png',
    placeId: 'static-simply-south-irving',
    mapsUrl: 'https://www.google.com/maps/search/Simply+South+1001+MacArthur+Park+Dr+Irving+TX+75063',
    cuisines: ['South Indian'],
  },
  {
    id: 'static-simply-south-frisco',
    name: 'Simply South',
    location: 'Frisco',
    city: 'Frisco',
    address: '8250 State Hwy 121, Frisco, TX 75034',
    rating: 4.0,
    reviewCount: 320,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782279204651-wztcs6.png',
    placeId: 'static-simply-south-frisco',
    mapsUrl: 'https://www.google.com/maps/search/Simply+South+8250+State+Hwy+121+Frisco+TX+75034',
    cuisines: ['South Indian'],
  },
  {
    id: 'static-nags',
    name: 'Nags Kitchen',
    location: 'Prosper',
    city: 'Prosper',
    address: '2111 E University Dr #60, Prosper, TX 75078',
    rating: 4.3,
    reviewCount: 320,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782092338980-2q5an.jpg',
    placeId: 'static-nags',
    mapsUrl: 'https://www.google.com/maps/search/Nags+Kitchen+2111+E+University+Dr+Prosper+TX+75078',
    cuisines: ['South Indian'],
  },
  {
    id: 'static-9',
    name: 'Hyderabad House',
    location: 'Allen',
    city: 'Allen',
    address: '939 W Stacy Rd, Allen, TX 75013',
    rating: 4.4,
    reviewCount: 980,
    image: 'https://images.unsplash.com/photo-1606471191009-63994c53433b?w=600&h=400&fit=crop',
    placeId: 'static-9',
    mapsUrl: 'https://www.google.com/maps/search/Hyderabad+House+Allen+TX',
    cuisines: ['South Indian'],
  },
  {
    id: 'static-10',
    name: 'Hyderabad House',
    location: 'Prosper',
    city: 'Prosper',
    address: '1000 W Prosper Trail, Prosper, TX 75078',
    rating: 4.4,
    reviewCount: 860,
    image: 'https://images.unsplash.com/photo-1606471191009-63994c53433b?w=600&h=400&fit=crop',
    placeId: 'static-10',
    mapsUrl: 'https://www.google.com/maps/search/Hyderabad+House+Prosper+TX',
    cuisines: ['South Indian'],
  },
  {
    id: 'static-11',
    name: 'The Monks',
    location: 'Aubrey',
    city: 'Aubrey',
    address: '26735 US-380 #130, Aubrey, TX 76227',
    rating: 4.1,
    reviewCount: 480,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782276327647-32mix.jpg',
    placeId: 'static-11',
    mapsUrl: 'https://www.google.com/maps/search/The+Monks+Aubrey+TX',
    cuisines: ['Fusion'],
  },
  {
    id: 'static-12',
    name: 'The Monks',
    location: 'Allen',
    city: 'Allen',
    address: '190 E Stacy Rd, Allen, TX 75002',
    rating: 4.1,
    reviewCount: 510,
    image: 'https://images.unsplash.com/photo-1631452180519-c014fe946bc7?w=600&h=400&fit=crop',
    placeId: 'static-12',
    mapsUrl: 'https://www.google.com/maps/search/The+Monks+Allen+TX',
    cuisines: ['Fusion'],
  },
  {
    id: 'static-13',
    name: 'Sanjh',
    location: 'Irving',
    city: 'Irving',
    address: "5250 N O'Connor Blvd Ste 146, Irving, TX 75039",
    rating: 4.3,
    reviewCount: 620,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782148124761-qvycil.jpg',
    placeId: 'static-13',
    mapsUrl: "https://www.google.com/maps/search/Sanjh+5250+N+O'Connor+Blvd+Ste+146+Irving+TX+75039",
    cuisines: ['North Indian'],
  },
  {
    id: 'static-14',
    name: 'Universal Bakery',
    location: 'Irving',
    city: 'Irving',
    address: '3100 W Royal Ln, Irving, TX 75063',
    rating: 4.5,
    reviewCount: 840,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782092452007-gyb32.jpg',
    placeId: 'static-14',
    mapsUrl: 'https://www.google.com/maps/search/Universal+Bakery+Irving+TX',
    cuisines: ['Bakery', 'Sweets'],
  },
  {
    id: 'static-15',
    name: 'HashTag India',
    location: 'Irving',
    city: 'Irving',
    address: '2625 Old Denton Rd, Irving, TX 75062',
    rating: 4.2,
    reviewCount: 530,
    image: 'https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=600&h=400&fit=crop',
    placeId: 'static-15',
    mapsUrl: 'https://www.google.com/maps/search/HashTag+India+Irving+TX',
    cuisines: ['Street Food'],
  },
  {
    id: 'static-16',
    name: '101 Paratha',
    location: 'Irving',
    city: 'Irving',
    address: '3311 Regent Blvd #121, Irving, TX 75063',
    rating: 4.4,
    reviewCount: 710,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782145941696-ts7up3.jpg',
    placeId: 'static-16',
    mapsUrl: 'https://www.google.com/maps/search/101+Paratha+3311+Regent+Blvd+Irving+TX+75063',
    cuisines: ['North Indian'],
  },
  {
    id: 'static-17',
    name: 'Bawarchi',
    location: 'Irving',
    city: 'Irving',
    address: '8150 N MacArthur Blvd, Irving, TX 75063',
    rating: 4.3,
    reviewCount: 890,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782139098718-9ddy9s.jpg',
    placeId: 'static-17',
    mapsUrl: 'https://www.google.com/maps/search/Bawarchi+Irving+TX',
    cuisines: ['Hyderabadi', 'Biryani'],
  },
  {
    id: 'static-18',
    name: 'The Delhi Lounge',
    location: 'Irving',
    city: 'Irving',
    address: '1850 Market Pl Blvd, Irving, TX 75063',
    rating: 4.1,
    reviewCount: 460,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782145243431-p5yi04.jpg',
    placeId: 'static-18',
    mapsUrl: 'https://www.google.com/maps/search/The+Delhi+Lounge+Irving+TX',
    cuisines: ['North Indian', 'Mughlai'],
  },
  {
    id: 'static-19',
    name: 'Anjappar Elite',
    location: 'Irving',
    city: 'Irving',
    address: '8080 Walton Blvd Ste 120, Irving, TX 75063',
    rating: 4.4,
    reviewCount: 950,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782091346578-g3zsm.jpg',
    placeId: 'static-19',
    mapsUrl: 'https://www.google.com/maps/search/Anjappar+Elite+8080+Walton+Blvd+Irving+TX',
    cuisines: ['South Indian', 'Chettinad'],
  },
  {
    id: 'static-20',
    name: 'Soul Of South',
    location: 'Irving',
    city: 'Irving',
    address: '6561 Riverside Dr Ste 110, Irving, TX 75039',
    rating: 4.2,
    reviewCount: 580,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782092741976-h8zygp.jpg',
    placeId: 'static-20',
    mapsUrl: 'https://www.google.com/maps/search/Soul+Of+South+6561+Riverside+Dr+Irving+TX+75039',
    cuisines: ['South Indian'],
  },
  {
    id: 'static-21',
    name: 'Bharat Bhavan',
    location: 'Irving',
    city: 'Irving',
    address: '530 W Lyndon B Johnson Fwy, Irving, TX 75063',
    rating: 4.3,
    reviewCount: 670,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782146091986-52mxyb.jpg',
    placeId: 'static-21',
    mapsUrl: 'https://www.google.com/maps/search/Bharat+Bhavan+530+W+Lyndon+B+Johnson+Fwy+Irving+TX+75063',
    cuisines: ['South Indian'],
  },
  {
    id: 'static-22',
    name: 'MNM Lounge',
    location: 'Irving',
    city: 'Irving',
    address: '1900 Market Pl Blvd Ste 110, Irving, TX 75063',
    rating: 4.0,
    reviewCount: 390,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782092645038-6z9gjp.jpg',
    placeId: 'static-22',
    mapsUrl: 'https://www.google.com/maps/search/MNM+Lounge+Irving+TX',
    cuisines: ['Bar'],
  },
  {
    id: 'static-24',
    name: 'Marina',
    location: 'Frisco',
    city: 'Frisco',
    address: '4617 Coit Rd Unit 130, Frisco, TX 75035',
    rating: 4.4,
    reviewCount: 680,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782093631304-rpylw.jpg',
    placeId: 'static-24',
    mapsUrl: 'https://www.google.com/maps/search/Marina+Frisco+TX',
    cuisines: ['Seafood'],
  },
  {
    id: 'static-26',
    name: 'Rotate Social',
    location: 'Frisco',
    city: 'Frisco',
    address: '5454 Main St #123, Frisco, TX 75033',
    rating: 4.3,
    reviewCount: 590,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782092538028-ubuxrf.jpg',
    placeId: 'static-26',
    mapsUrl: 'https://www.google.com/maps/search/Rotate+Social+Frisco+TX',
    cuisines: ['Fusion'],
  },
  {
    id: 'static-27',
    name: 'Frisco BaseCamp',
    location: 'Frisco',
    city: 'Frisco',
    address: '2552 Stonebrook Pkwy #302, Frisco, TX 75034',
    rating: 4.2,
    reviewCount: 470,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782146750500-40okf.jpg',
    placeId: 'static-27',
    mapsUrl: 'https://www.google.com/maps/search/Frisco+BaseCamp+Frisco+TX',
    cuisines: ['American', 'Fusion'],
  },
  {
    id: 'static-28',
    name: 'Sri Krishna Vilas',
    location: 'Frisco',
    city: 'Frisco',
    address: '13215 Main St, Frisco, TX 75035',
    rating: 4.4,
    reviewCount: 820,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782145664465-212vmx.jpg',
    placeId: 'static-28',
    mapsUrl: 'https://www.google.com/maps/search/Sri+Krishna+Vilas+Frisco+TX',
    cuisines: ['South Indian', 'Vegetarian'],
  },
  {
    id: 'static-29',
    name: 'Kailash Parbat',
    location: 'Frisco',
    city: 'Frisco',
    address: '8990 Coit Rd Ste 100, Frisco, TX 75035',
    rating: 4.3,
    reviewCount: 740,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782093418084-rxtuhm.jpg',
    placeId: 'static-29',
    mapsUrl: 'https://www.google.com/maps/search/8990+Coit+Rd+Ste+100+Frisco+TX+75035',
    cuisines: ['Street Food', 'Vegetarian'],
  },
  {
    id: 'static-30',
    name: 'Bharat Bhavan',
    location: 'Frisco',
    city: 'Frisco',
    address: '3355 Main St, Frisco, TX 75035',
    rating: 4.2,
    reviewCount: 560,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782146552887-ra8um9.png',
    placeId: 'static-30',
    mapsUrl: 'https://www.google.com/maps/search/Bharat+Bhavan+Frisco+TX',
    cuisines: ['South Indian'],
  },
  {
    id: 'static-31',
    name: 'Spicy Vihaar',
    location: 'Frisco',
    city: 'Frisco',
    address: '4235 Preston Rd Ste 100, Frisco, TX 75034',
    rating: 4.1,
    reviewCount: 430,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782144964358-pwelop.jpg',
    placeId: 'static-31',
    mapsUrl: 'https://www.google.com/maps/search/Spicy+Vihaar+Frisco+TX',
    cuisines: ['Street Food'],
  },
  {
    id: 'static-32',
    name: 'Fanzo Sports Bar',
    location: 'Frisco',
    city: 'Frisco',
    address: '5977 Preston Rd Retail Building 1, Suite 100, Frisco, TX 75034',
    rating: 4.0,
    reviewCount: 380,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782093001415-hlcemm.jpg',
    placeId: 'static-32',
    mapsUrl: 'https://www.google.com/maps/search/Fanzo+Sports+Bar+Frisco+TX',
    cuisines: ['Bar & Grill', 'American'],
  },
  {
    id: 'static-34',
    name: 'The Retro Street Bar & Kitchen',
    location: 'Frisco',
    city: 'Frisco',
    address: '307 Main St #105, Frisco, TX 75036',
    rating: 4.3,
    reviewCount: 620,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782145785534-k6q06.jpg',
    placeId: 'static-34',
    mapsUrl: 'https://www.google.com/maps/search/The+Retro+Street+Bar+and+Kitchen+307+Main+St+Frisco+TX+75036',
    cuisines: ['Bar & Grill', 'Street Food', 'Fusion'],
  },
  {
    id: 'static-35',
    name: 'Gwalia',
    location: 'Frisco',
    city: 'Frisco',
    address: '2552 Stonebrook Pkwy #302, Frisco, TX 75034',
    rating: 4.4,
    reviewCount: 790,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782146879864-appm3.jpg',
    placeId: 'static-35',
    mapsUrl: 'https://www.google.com/maps/search/Gwalia+Frisco+TX',
    cuisines: ['Sweets', 'Bakery', 'Vegetarian'],
  },
  {
    id: 'static-36',
    name: 'Saravanaa Bhavan',
    location: 'Plano',
    city: 'Plano',
    address: '8908 Ohio Dr, Plano, TX 75024',
    rating: 4.5,
    reviewCount: 1200,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782148226654-khwcr.jpg',
    placeId: 'static-36',
    mapsUrl: 'https://www.google.com/maps/search/Saravanaa+Bhavan+8908+Ohio+Dr+Plano+TX+75024',
    cuisines: ['South Indian', 'Vegetarian'],
  },
  {
    id: 'static-saravanaa-bhavan-irving',
    name: 'Saravanaa Bhavan',
    location: 'Irving',
    city: 'Irving',
    address: '8604 N MacArthur Blvd, Irving, TX 75063',
    rating: 4.5,
    reviewCount: 1200,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782148940676-xxfeie.jpg',
    placeId: 'static-saravanaa-bhavan-irving',
    mapsUrl: 'https://www.google.com/maps/search/Saravanaa+Bhavan+8604+N+MacArthur+Blvd+Irving+TX+75063',
    cuisines: ['South Indian', 'Vegetarian'],
  },
  {
    id: 'static-37',
    name: 'Swadeshi',
    location: 'Frisco',
    city: 'Frisco',
    address: '14300 State Hwy 121 #100, Frisco, TX 75035',
    rating: 4.2,
    reviewCount: 490,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782147718108-zdlyb5j.jpg',
    placeId: 'static-37',
    mapsUrl: 'https://www.google.com/maps/search/Swadeshi+14300+State+Hwy+121+Frisco+TX+75035',
    cuisines: [],
  },
  {
    id: 'static-swadeshi-lewisville',
    name: 'Swadeshi',
    location: 'Lewisville',
    city: 'Lewisville',
    address: '112 Castle Hills Dr, Lewisville, TX 75056',
    rating: 4.2,
    reviewCount: 15,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782147809282-88xje6.jpg',
    placeId: 'static-swadeshi-lewisville',
    mapsUrl: 'https://www.google.com/maps/search/Swadeshi+112+Castle+Hills+Dr+Lewisville+TX+75056',
    cuisines: ['Grocery'],
  },
  {
    id: 'static-38',
    name: 'The Mango Yard',
    location: 'Frisco',
    city: 'Frisco',
    address: '12275 E University Dr Ste 100, Frisco, TX 75035',
    rating: 4.3,
    reviewCount: 670,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782091227281-3eca9o9.jpg',
    placeId: 'static-38',
    mapsUrl: 'https://www.google.com/maps/search/The+Mango+Yard+Frisco+TX',
    cuisines: ['Fusion'],
  },
  {
    id: 'static-39',
    name: 'Yum Of India',
    location: 'McKinney',
    city: 'McKinney',
    address: '1480 Independence Pkwy Ste #280, McKinney, TX 75072',
    rating: 4.1,
    reviewCount: 410,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782092855676-fp85sv.jpg',
    placeId: 'static-39',
    mapsUrl: 'https://www.google.com/maps/search/Yum+Of+India+McKinney+TX',
    cuisines: ['Street Food'],
  },
  {
    id: 'static-40',
    name: 'Dum N Grill',
    location: 'Frisco',
    city: 'Frisco',
    address: '14339 Eldorado Pkwy Ste 130, Frisco, TX 75035',
    rating: 4.3,
    reviewCount: 580,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782276481770-yul4g8.jpg',
    placeId: 'static-40',
    mapsUrl: 'https://www.google.com/maps/search/Dum+N+Grill+Frisco+TX',
    cuisines: ['Hyderabadi', 'Biryani', 'Grill'],
  },
  {
    id: 'static-41',
    name: "Rajula's Kitchen",
    location: 'Frisco',
    city: 'Frisco',
    address: '14339 Eldorado Pkwy Ste - 120, Frisco, TX 75035',
    rating: 4.2,
    reviewCount: 450,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782153722732-scyswn.jpg',
    placeId: 'static-41',
    mapsUrl: "https://www.google.com/maps/search/Rajula's+Kitchen+14339+Eldorado+Pkwy+Frisco+TX+75035",
    cuisines: ['Home Style'],
  },
  {
    id: 'static-42',
    name: 'Red Hot Chilli Pepper',
    location: 'Frisco',
    city: 'Frisco',
    address: '8549 Gaylord Pkwy #109, Frisco, TX 75034',
    rating: 4.3,
    reviewCount: 520,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782094139348-fudxtk.jpg',
    placeId: 'static-42',
    mapsUrl: 'https://www.google.com/maps/search/Red+Hot+Chilli+Pepper+Frisco+TX',
    cuisines: ['North Indian', 'Fusion'],
  },
  {
    id: 'static-43',
    name: 'Kuppanna',
    location: 'Plano',
    city: 'Plano',
    address: '1301 Custer Rd #510a, Plano, TX 75075',
    rating: 4.4,
    reviewCount: 780,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782093492092-pd5j7j.jpg',
    placeId: 'static-43',
    mapsUrl: 'https://www.google.com/maps/search/1301+Custer+Rd+510a+Plano+TX+75075',
    cuisines: ['South Indian', 'Chettinad'],
  },
  {
    id: 'static-44',
    name: 'Bawarchi',
    location: 'Plano',
    city: 'Plano',
    address: '3400 W Campbell Rd, Richardson, TX 75080',
    rating: 4.3,
    reviewCount: 920,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782139410567-b4cyu8.jpg',
    placeId: 'static-44',
    mapsUrl: 'https://www.google.com/maps/search/Bawarchi+Plano+TX',
    cuisines: ['Hyderabadi', 'Biryani'],
  },
  {
    id: 'static-45',
    name: 'Anjappar Elite',
    location: 'Plano',
    city: 'Plano',
    address: '6500 W Park Blvd, Plano, TX 75093',
    rating: 4.4,
    reviewCount: 860,
    image: 'https://images.unsplash.com/photo-1606471191009-63994c53433b?w=600&h=400&fit=crop',
    placeId: 'static-45',
    mapsUrl: 'https://www.google.com/maps/search/Anjappar+Elite+Plano+TX',
    cuisines: ['South Indian', 'Chettinad'],
  },
  {
    id: 'static-46',
    name: 'Dil Se Desi',
    location: 'Plano',
    city: 'Plano',
    address: '1500 W Spring Creek Pkwy, Plano, TX 75093',
    rating: 4.2,
    reviewCount: 650,
    image: 'https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=600&h=400&fit=crop',
    placeId: 'static-46',
    mapsUrl: 'https://www.google.com/maps/search/Dil+Se+Desi+Plano+TX',
    cuisines: ['Street Food'],
  },
  {
    id: 'static-47',
    name: 'DesiBites',
    location: 'Plano',
    city: 'Plano',
    address: '5588 State Hwy 121 #300, Plano, TX 75024',
    rating: 4.1,
    reviewCount: 480,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782092088681-fvmbke.jpg',
    placeId: 'static-47',
    mapsUrl: 'https://www.google.com/maps/search/DesiBites+Plano+TX',
    cuisines: ['Fusion'],
  },
  {
    id: 'static-48',
    name: 'Cafe De Khan',
    location: 'Plano',
    city: 'Plano',
    address: '2040 W Spring Creek Pkwy Ste 136, Plano, TX 75023',
    rating: 4.3,
    reviewCount: 590,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782146662380-sopj8f.jpg',
    placeId: 'static-48',
    mapsUrl: 'https://www.google.com/maps/search/Cafe+De+Khan+Plano+TX',
    cuisines: ['Pakistani', 'Mughlai'],
  },
  {
    id: 'static-49',
    name: 'Bawarchi Biryanis',
    location: 'Plano',
    city: 'Plano',
    address: '8201 Ohio Dr #110, Plano, TX 75024',
    rating: 4.4,
    reviewCount: 980,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782144028810-1r3tzs.jpg',
    placeId: 'static-49',
    mapsUrl: 'https://www.google.com/maps/search/Bawarchi+Biryanis+8201+Ohio+Dr+Plano+TX+75024',
    cuisines: ['Hyderabadi', 'Biryani'],
  },
  {
    id: 'static-50',
    name: 'Tantra',
    location: 'McKinney',
    city: 'McKinney',
    address: '6675 Mediterranean Dr Ste 3120, McKinney, TX 75072',
    rating: 4.3,
    reviewCount: 420,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782145843946-52k7fo.jpg',
    placeId: 'static-56',
    mapsUrl: 'https://www.google.com/maps/search/Shiva+Bar+Grill+Dallas+TX',
    cuisines: ['Indian', 'Bar & Grill'],
  },
  {
    id: 'static-57',
    name: 'BK Khan BBQ',
    location: 'Farmers Branch',
    city: 'Farmers Branch',
    address: '13608 Midway Rd, Farmers Branch, TX 75244',
    rating: 4.3,
    reviewCount: 380,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782146621381-sk6vpq.jpg',
    placeId: 'static-57',
    mapsUrl: 'https://www.google.com/maps/search/BK+Khan+BBQ+Dallas+TX',
    cuisines: ['BBQ', 'Indian'],
  },
  {
    id: 'static-58',
    name: 'WindMills',
    location: 'The Colony',
    city: 'The Colony',
    address: '5755 Grandscape Blvd, The Colony, TX 75056',
    rating: 4.5,
    reviewCount: 410,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782149076184-7f08sh.jpg',
    placeId: 'static-58',
    mapsUrl: 'https://www.google.com/maps/search/WindMills+5755+Grandscape+Blvd+The+Colony+TX+75056',
    cuisines: ['Indian', 'Multi-Cuisine'],
  },
  {
    id: 'static-59',
    name: 'Sheesh Eatery & Lounge',
    location: 'The Colony',
    city: 'The Colony',
    address: '4707 State Hwy 121, The Colony, TX 75056',
    rating: 4.4,
    reviewCount: 360,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782147406163-03q75.jpg',
    placeId: 'static-59',
    mapsUrl: 'https://www.google.com/maps/search/Sheesh+The+Colony+TX',
    cuisines: ['Indian', 'Bar & Grill'],
  },
  {
    id: 'static-64',
    name: 'Mai Colachi',
    location: 'Plano',
    city: 'Plano',
    address: '6205 Coit Rd #396, Plano, TX 75024',
    rating: 4.5,
    reviewCount: 420,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782147753440-sje0bx.jpg',
    placeId: 'static-64',
    mapsUrl: 'https://www.google.com/maps/search/Mai+Colachi+6205+Coit+Rd+%23396+Plano+TX+75024',
    cuisines: ['Pakistani', 'BBQ'],
  },
  {
    id: 'static-65',
    name: 'Al Markaz',
    location: 'Carrollton',
    city: 'Carrollton',
    address: '2660 E Trinity Mills Rd, Carrollton, TX 75006',
    rating: 4.5,
    reviewCount: 380,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782146006668-21jrt.jpg',
    placeId: 'static-65',
    mapsUrl: 'https://www.google.com/maps/search/Al+Markaz+Carrollton+TX',
    cuisines: ['Pakistani', 'BBQ'],
  },
  {
    id: 'static-66',
    name: 'Gilgamesh',
    location: 'Richardson',
    city: 'Richardson',
    address: '300 Terrace Dr #301, Richardson, TX 75081',
    rating: 4.0,
    reviewCount: 0,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782145303459-8i9zxf.jpg',
    placeId: 'static-66',
    mapsUrl: 'https://www.google.com/maps/search/Gilgamesh+300+Terrace+Dr+Richardson+TX+75081',
    cuisines: ['Mediterranean'],
  },
  {
    id: 'static-67',
    name: 'Prime Doner',
    location: 'Plano',
    city: 'Plano',
    address: '601 W 15th St, Plano, TX 75075',
    rating: 4.3,
    reviewCount: 0,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782093974595-990s7n.jpg',
    placeId: 'static-67',
    mapsUrl: 'https://www.google.com/maps/search/Prime+Doner+601+W+15th+St+Plano+TX+75075',
    cuisines: ['Mediterranean'],
  },
  {
    id: 'static-68',
    name: 'Habibi & Spice',
    location: 'Lewisville',
    city: 'Lewisville',
    address: '4770 State Hwy 121, Lewisville, TX 75056',
    rating: 4.2,
    reviewCount: 0,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782145336589-p2q4mf.jpg',
    placeId: 'static-68',
    mapsUrl: 'https://www.google.com/maps/search/Habibi+Spice+4770+State+Hwy+121+Lewisville+TX+75056',
    cuisines: ['Mediterranean'],
  },
  {
    id: 'static-69',
    name: 'LaPasha Lounge',
    location: 'Addison',
    city: 'Addison',
    address: '3855 Belt Line Rd, Addison, TX 75001',
    rating: 4.1,
    reviewCount: 0,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782146974416-ep1lsnj.jpg',
    placeId: 'static-69',
    mapsUrl: 'https://www.google.com/maps/search/LaPasha+Lounge+3855+Belt+Line+Rd+Addison+TX+75001',
    cuisines: ['Mediterranean'],
  },
  {
    id: 'static-70',
    name: 'Sigree Grill',
    location: 'Addison',
    city: 'Addison',
    address: '4145 Belt Line Rd Ste 218, Addison, TX 75001',
    rating: 4.2,
    reviewCount: 0,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782142674364-1yiyx.jpg',
    placeId: 'static-70',
    mapsUrl: 'https://www.google.com/maps/search/Sigree+Grill+4145+Belt+Line+Rd+Ste+218+Addison+TX+75001',
    cuisines: ['North Indian'],
  },
  {
    id: 'static-veg-vihaar',
    name: 'Veg Vihaar',
    location: 'Little Elm',
    city: 'Little Elm',
    address: '3010 FM 423 #500, Little Elm, TX 75068',
    rating: 4.3,
    reviewCount: 280,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782149193674-clejrb.jpg',
    placeId: 'static-veg-vihaar',
    mapsUrl: 'https://www.google.com/maps/search/Veg+Vihaar+3010+FM+423+Little+Elm+TX+75068',
    cuisines: ['Vegetarian', 'South Indian'],
  },
  {
    id: 'static-golisoda1',
    name: 'Goli Soda',
    location: 'McKinney',
    city: 'McKinney',
    address: '502 W University Dr, McKinney, TX 75069',
    rating: 4.5,
    reviewCount: 120,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782149300450-d6gz3i.jpg',
    placeId: 'static-golisoda1',
    mapsUrl: 'https://www.google.com/maps/search/Goli+Soda+502+W+University+Dr+McKinney+TX+75069',
    cuisines: ['Indian', 'Fast Food'],
  },
  {
    id: 'static-elysium',
    name: 'Elysium - Live Music Bar',
    location: 'McKinney',
    city: 'McKinney',
    address: '7550 State Hwy 121 Ste 100, McKinney, TX 75070',
    rating: 4.6,
    reviewCount: 150,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782156113008-5j96gx.jpg',
    placeId: 'static-elysium',
    mapsUrl: 'https://www.google.com/maps/search/Elysium+Live+Music+Bar+7550+State+Hwy+121+McKinney+TX+75070',
    cuisines: ['Bar', 'Live Music', 'American'],
  },
  {
    id: 'static-the-mirage-lounge',
    name: 'The Mirage Lounge',
    location: 'McKinney',
    city: 'McKinney',
    address: '3750 McKinney Ranch Pkwy Ste 100, McKinney, TX 75070',
    rating: 4.5,
    reviewCount: 130,
    image: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-24/6da68dc8c08d.png',
    placeId: 'static-the-mirage-lounge',
    mapsUrl: 'https://www.google.com/maps/search/The+Mirage+Lounge+3750+McKinney+Ranch+Pkwy+McKinney+TX+75070',
    cuisines: ['Bar', 'Lounge', 'American'],
  },
  {
    id: 'static-delhi6',
    name: 'Delhi6',
    location: 'Highland Village',
    city: 'Highland Village',
    address: '2420 Justin Rd, Highland Village, TX 75077',
    rating: 4.3,
    reviewCount: 85,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782277128316-dwsbgk.jpg',
    placeId: 'static-delhi6',
    mapsUrl: 'https://www.google.com/maps/search/Delhi6+2420+Justin+Rd+Highland+Village+TX+75077',
    cuisines: ['Street Food'],
  },
  {
    id: 'static-huq-up',
    name: 'Huq Up',
    location: 'Little Elm',
    city: 'Little Elm',
    address: '27052 E University Dr Ste 100, Little Elm, TX 75068',
    rating: 4.4,
    reviewCount: 95,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782279579703-1vpnpx.png',
    placeId: 'static-huq-up',
    mapsUrl: 'https://www.google.com/maps/search/Huq+Up+27052+E+University+Dr+Little+Elm+TX+75068',
    cuisines: ['Indian', 'Bar', 'Lounge', 'Hookah Lounge'],
  },
  {
    id: 'static-ghazal-lounge',
    name: 'Ghazal Lounge and Bar',
    location: 'McKinney',
    city: 'McKinney',
    address: '4051 S Custer Rd #1110, McKinney, TX 75070',
    rating: 4.4,
    reviewCount: 110,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782152377431-2uqz4m.jpg',
    placeId: 'static-ghazal-lounge',
    mapsUrl: 'https://www.google.com/maps/search/Ghazal+Lounge+and+Bar+4051+S+Custer+Rd+McKinney+TX+75070',
    cuisines: ['Bar', 'Lounge', 'Hookah Lounge'],
  },
  {
    id: 'static-cavalli',
    name: 'Cavalli',
    location: 'Dallas',
    city: 'Dallas',
    address: '18111 Dallas Pkwy #100, Dallas, TX 75287',
    rating: 4.5,
    reviewCount: 125,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782152169373-pf4g4g.jpg',
    placeId: 'static-cavalli',
    mapsUrl: 'https://www.google.com/maps/search/Cavalli+18111+Dallas+Pkwy+Dallas+TX+75287',
    cuisines: ['Italian', 'Fine Dining', 'Hookah Lounge'],
  },
  {
    id: 'static-al-amir',
    name: 'Al-Amir',
    location: 'Addison',
    city: 'Addison',
    address: '3885 Belt Line Rd, Addison, TX 75001',
    rating: 4.3,
    reviewCount: 100,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782152297921-riuol.jpg',
    placeId: 'static-al-amir',
    mapsUrl: 'https://www.google.com/maps/search/Al-Amir+3885+Belt+Line+Rd+Addison+TX+75001',
    cuisines: ['Mediterranean', 'Middle Eastern'],
  },
  {
    id: 'static-red-hot',
    name: 'Red Hot',
    location: 'Richardson',
    city: 'Richardson',
    address: '744 S Central Expy Ste #230, Richardson, TX 75080',
    rating: 4.2,
    reviewCount: 95,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782277000476-8d15er.jpg',
    placeId: 'static-red-hot',
    mapsUrl: 'https://www.google.com/maps/search/Red+Hot+744+S+Central+Expy+Richardson+TX+75080',
    cuisines: ['North Indian', 'Fusion'],
  },
  {
    id: 'static-ghazal',
    name: 'Ghazal Lounge and Bar',
    location: 'McKinney',
    city: 'McKinney',
    address: '4051 S Custer Rd #1110, McKinney, TX 75070',
    rating: 4.1,
    reviewCount: 90,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782152377431-2uqz4m.jpg',
    placeId: 'static-ghazal',
    mapsUrl: 'https://www.google.com/maps/search/Ghazal+Lounge+and+Bar+4051+S+Custer+Rd+McKinney+TX+75070',
    cuisines: ['Indian', 'Bar', 'Lounge', 'Hookah Lounge'],
  },
  {
    id: 'static-mirage-lounge',
    name: 'The Mirage Lounge',
    location: 'McKinney',
    city: 'McKinney',
    address: '3750 McKinney Ranch Pkwy Ste 100, McKinney, TX 75070',
    rating: 4.0,
    reviewCount: 75,
    image: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-24/6da68dc8c08d.png',
    placeId: 'static-mirage-lounge',
    mapsUrl: 'https://www.google.com/maps/search/The+Mirage+Lounge+3750+McKinney+Ranch+Pkwy+McKinney+TX+75070',
    cuisines: ['Indian', 'Bar', 'Lounge', 'Hookah Lounge'],
  },
  {
    id: 'static-huq-up',
    name: 'Huq Up',
    location: 'Little Elm',
    city: 'Little Elm',
    address: '27052 E University Dr Ste 100, Little Elm, TX 75068',
    rating: 4.0,
    reviewCount: 65,
    image: 'https://images.unsplash.com/photo-1552566626-52f8b828add9?w=600&h=400&fit=crop',
    placeId: 'static-huq-up',
    mapsUrl: 'https://www.google.com/maps/search/Huq+Up+27052+E+University+Dr+Little+Elm+TX+75068',
    cuisines: ['Indian', 'Fusion', 'Hookah Lounge'],
  },
  {
    id: 'static-jashan',
    name: 'Jashan',
    location: 'Plano',
    city: 'Plano',
    address: '7401 Lone Star Dr Ste B 120, Plano, TX 75024',
    rating: 4.0,
    reviewCount: 50,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782158022967-vtuvyt.jpg',
    placeId: 'static-jashan',
    mapsUrl: 'https://www.google.com/maps/search/Jashan+7401+Lone+Star+Dr+Ste+B+120+Plano+TX+75024',
    cuisines: ['Indian'],
  },
  {
    id: 'static-urban-tadka',
    name: 'Urban Tadka',
    location: 'Irving',
    city: 'Irving',
    address: '1800 Market Pl Blvd #190, Irving, TX 75063',
    rating: 4.0,
    reviewCount: 50,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782276923573-lnuj3g.jpg',
    placeId: 'static-urban-tadka',
    mapsUrl: 'https://www.google.com/maps/search/Urban+Tadka+1800+Market+Pl+Blvd+190+Irving+TX+75063',
    cuisines: ['Indian'],
  },
  {
    id: 'static-spice-rack-plano',
    name: 'Spice Rack Plano',
    location: 'Plano',
    city: 'Plano',
    address: '2865 McDermott Rd #105, Plano, TX 75025',
    rating: 4.0,
    reviewCount: 50,
    image: 'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-22/67044519250b.png',
    placeId: 'static-spice-rack-plano',
    mapsUrl: 'https://www.google.com/maps/search/Spice+Rack+2865+McDermott+Rd+105+Plano+TX+75025',
    cuisines: ['Indian'],
  },
  {
    id: 'static-kailash-parbat',
    name: 'Kailash Parbat',
    location: 'Frisco',
    city: 'Frisco',
    address: '8990 Coit Rd Ste 100, Frisco, TX 75035',
    rating: 4.2,
    reviewCount: 300,
    image: 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=600&h=400&fit=crop',
    placeId: 'static-kailash-parbat',
    mapsUrl: 'https://www.google.com/maps/search/Kailash+Parbat+8990+Coit+Rd+Frisco+TX+75035',
    cuisines: ['North Indian', 'Vegetarian'],
  },
  {
    id: 'static-gwalia',
    name: 'Gwalia',
    location: 'Frisco',
    city: 'Frisco',
    address: '2552 Stonebrook Pkwy #302, Frisco, TX 75034',
    rating: 4.1,
    reviewCount: 250,
    image: 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=600&h=400&fit=crop',
    placeId: 'static-gwalia',
    mapsUrl: 'https://www.google.com/maps/search/Gwalia+2552+Stonebrook+Pkwy+Frisco+TX+75034',
    cuisines: ['North Indian', 'Vegetarian'],
  },
  {
    id: 'static-rajulas-kitchen',
    name: "Rajula's Kitchen",
    location: 'Frisco',
    city: 'Frisco',
    address: '14339 Eldorado Pkwy Ste - 120, Frisco, TX 75035',
    rating: 4.3,
    reviewCount: 350,
    image: 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=600&h=400&fit=crop',
    placeId: 'static-rajulas-kitchen',
    mapsUrl: 'https://www.google.com/maps/search/Rajula\'s+Kitchen+14339+Eldorado+Pkwy+Frisco+TX+75035',
    cuisines: ['North Indian', 'Vegetarian'],
  },
  {
    id: 'static-hh-prosper-university',
    name: 'Hyderabad House',
    location: 'Prosper',
    city: 'Prosper',
    address: '4740 W University Dr Ste 20, Prosper, TX 75078',
    rating: 4.4,
    reviewCount: 850,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782276165236-5lanbn.jpg',
    placeId: 'static-hh-prosper-university',
    mapsUrl: 'https://www.google.com/maps/search/Hyderabad+House+4740+W+University+Dr+Prosper+TX+75078',
    cuisines: ['South Indian'],
  },
  {
    id: 'static-dc-frisco',
    name: 'Desi Chowrasta',
    location: 'Frisco',
    city: 'Frisco',
    address: '15851 Rolater Rd, Frisco, TX 75035',
    rating: 4.2,
    reviewCount: 400,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783373001971-ahet5t.png',
    placeId: 'static-dc-frisco',
    mapsUrl: 'https://www.google.com/maps/search/Desi+Chowrasta+15851+Rolater+Rd+Frisco+TX+75035',
    cuisines: ['South Indian', 'North Indian'],
  },
  {
    id: 'static-dc-coppell',
    name: 'Desi Chowrasta',
    location: 'Coppell',
    city: 'Coppell',
    address: '121 State Hwy 121, Coppell, TX 75019',
    rating: 4.2,
    reviewCount: 380,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783456007809-qennpi.png',
    placeId: 'static-dc-coppell',
    mapsUrl: 'https://www.google.com/maps/search/Desi+Chowrasta+121+State+Hwy+121+Coppell+TX+75019',
    cuisines: ['South Indian', 'North Indian'],
  },
  {
    id: 'static-dc-lewisville',
    name: 'Desi Chowrasta',
    location: 'Lewisville',
    city: 'Lewisville',
    address: '4600 State Hwy 121, Lewisville, TX 75056',
    rating: 4.2,
    reviewCount: 360,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783456130244-r25kp2.png',
    placeId: 'static-dc-lewisville',
    mapsUrl: 'https://www.google.com/maps/search/Desi+Chowrasta+4600+State+Hwy+121+Lewisville+TX+75056',
    cuisines: ['South Indian', 'North Indian'],
  },
  {
    id: 'static-hi-irving-lbj',
    name: 'Hashtag India',
    location: 'Irving',
    city: 'Irving',
    address: '1102 W Lyndon B Johnson Fwy, Irving, TX 75063',
    rating: 4.2,
    reviewCount: 500,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782223920185-tudhij.jpg',
    placeId: 'static-hi-irving-lbj',
    mapsUrl: 'https://www.google.com/maps/search/Hashtag+India+1102+W+Lyndon+B+Johnson+Fwy+Irving+TX+75063',
    cuisines: ['South Indian', 'Street Food'],
  },
  {
    id: 'static-hi-mckinney',
    name: 'Hashtag India',
    location: 'McKinney',
    city: 'McKinney',
    address: '3510 W University Dr Ste 100, McKinney, TX 75071',
    rating: 4.2,
    reviewCount: 480,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782276234849-t3e7nn.jpg',
    placeId: 'static-hi-mckinney',
    mapsUrl: 'https://www.google.com/maps/search/Hashtag+India+3510+W+University+Dr+McKinney+TX+75071',
    cuisines: ['South Indian', 'Street Food'],
  },
  {
    id: 'static-monks-allen-exchange',
    name: 'The Monks',
    location: 'Allen',
    city: 'Allen',
    address: '790 W Exchange Pkwy #140, Allen, TX 75013',
    rating: 4.1,
    reviewCount: 490,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782276388971-rtd10zjf.jpg',
    placeId: 'static-monks-allen-exchange',
    mapsUrl: 'https://www.google.com/maps/search/The+Monks+790+W+Exchange+Pkwy+Allen+TX+75013',
    cuisines: ['Fusion'],
  },
  {
    id: 'static-monks-aubrey-380',
    name: 'The Monks',
    location: 'Aubrey',
    city: 'Aubrey',
    address: '26735 US-380 #130, Aubrey, TX 76227',
    rating: 4.1,
    reviewCount: 470,
    image: 'https://images.unsplash.com/photo-1631452180519-c014fe946bc7?w=600&h=400&fit=crop',
    placeId: 'static-monks-aubrey-380',
    mapsUrl: 'https://www.google.com/maps/search/The+Monks+26735+US+380+Aubrey+TX+76227',
    cuisines: ['Fusion'],
  },
  {
    id: 'static-shree-vimals',
    name: "Shree Vimal's",
    location: 'Irving',
    city: 'Irving',
    address: '535 W Airport Fwy Ste 100, Irving, TX 75062',
    rating: 4.0,
    reviewCount: 0,
    image: 'https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=600&h=400&fit=crop',
    placeId: 'static-shree-vimals',
    mapsUrl: 'https://www.google.com/maps/search/Shree+Vimals+535+W+Airport+Fwy+Ste+100+Irving+TX+75062',
    cuisines: ['Indian'],
  },
  {
    id: 'static-garam-masala-kitchen',
    name: 'Garam Masala Kitchen',
    location: 'Melissa',
    city: 'Melissa',
    address: '3310 Sam Rayburn Hwy Ste 500, Melissa, TX 75454',
    rating: 4.0,
    reviewCount: 0,
    image: 'https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783365106776-esph7.png',
    placeId: 'static-garam-masala-kitchen',
    mapsUrl: 'https://www.google.com/maps/search/Garam+Masala+Kitchen+3310+Sam+Rayburn+Hwy+Ste+500+Melissa+TX+75454',
    cuisines: ['Indian'],
  },
];

export function useGooglePlaces(locationFilter?: string) {
  const [places, setPlaces] = useState<PlaceResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const fetchedRef = useRef(false);

  const fetchPlaces = useCallback(async (force = false) => {
    if (fetchedRef.current && !force) return;
    fetchedRef.current = true;

    setLoading(true);
    setError(null);

    // Check cache first
    if (!force) {
      const cached = getCached(locationFilter);
      if (cached) {
        setPlaces(cached);
        setLoading(false);
        setUsingFallback(false);
        return;
      }
    }

    try {
      const results = await fetchPlacesFromAPI(locationFilter);
      setPlaces(results);
      setCached(locationFilter, results);
      setUsingFallback(false);
    } catch (err) {
      console.warn('Google Places API failed, using fallback:', err);
      // Filter fallback by location if needed
      let fallback = [...STATIC_FALLBACK];
      if (locationFilter && locationFilter !== 'All Locations') {
        fallback = fallback.filter(r => r.location === locationFilter);
      }
      setPlaces(sortByPriority(fallback));
      setUsingFallback(true);
      setError(null); // Don't show error since we have fallback
    } finally {
      setLoading(false);
    }
  }, [locationFilter]);

  useEffect(() => {
    fetchedRef.current = false;
    fetchPlaces();
  }, [fetchPlaces, locationFilter]);

  return { places, loading, error, usingFallback, refetch: () => fetchPlaces(true) };
}

export default useGooglePlaces;
